import { TitleHeading } from "@/components/helper/TitleHeading";
import "@/styles/faq.css";
import { BaseSection, BaseSectionProps } from "@/components/sections/BaseSection";
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { FiPlus } from "react-icons/fi";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { EmphasisText } from "@/components/helper/EmphasisText";
import { FAQ_ITEMS } from "@/config/faq";

export function FAQSection(props: BaseSectionProps) {
    const { t } = useTranslation();
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const prefersReducedMotion = usePrefersReducedMotion();

    const toggleItem = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <BaseSection {...props}>
            <div className="faq-layout">
                <div className="faq-head">
                    <TitleHeading className="title-heading mb-4">
                        {t('faq.title')}
                    </TitleHeading>
                    <h2 className="section-subtitle">{t('faq.subtitle')}</h2>
                    <p className="text-theme-light mt-4 faq-description">
                        {t('faq.description')}
                    </p>
                </div>

                <div className="faq-container">
                    {FAQ_ITEMS.map((item, index) => {
                        const isOpen = openIndex === index;
                        const panelId = `faq-panel-${index}`;
                        const buttonId = `faq-question-${index}`;

                        return (
                            <motion.div
                                className="faq-item"
                                key={index}
                                initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
                                animate={{
                                    opacity: props.section.viewHook.inView ? 1 : 0,
                                    y: props.section.viewHook.inView ? 0 : 24
                                }}
                                transition={{
                                    duration: 0.8,
                                    ease: [0.16, 1, 0.3, 1],
                                    delay: index * 0.05
                                }}
                            >
                                <h3 className="faq-question-heading">
                                    <button
                                        id={buttonId}
                                        className="faq-question"
                                        onClick={() => toggleItem(index)}
                                        aria-expanded={isOpen}
                                        aria-controls={panelId}
                                    >
                                        <span className="faq-question-text">
                                            {t(item.questionKey)}
                                        </span>
                                        <motion.span
                                            className="faq-icon"
                                            animate={prefersReducedMotion ? undefined : { rotate: isOpen ? 45 : 0 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <FiPlus size={18} />
                                        </motion.span>
                                    </button>
                                </h3>
                                <AnimatePresence initial={false}>
                                    {isOpen && (
                                        <motion.div
                                            id={panelId}
                                            role="region"
                                            aria-labelledby={buttonId}
                                            className="faq-answer-wrapper"
                                            initial={prefersReducedMotion ? false : { height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={prefersReducedMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
                                            transition={{ duration: 0.3, ease: "easeInOut" }}
                                        >
                                            <div className="faq-answer">
                                                <EmphasisText i18nKey={item.answerKey} />
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </BaseSection>
    );
}
